"use client";
import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { CalendarIcon, Clock, Mail, Phone, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { motion, AnimatePresence } from "framer-motion";
import {
  getBookingAvailability,
  bookMeeting,
  trackAnalyticsEvent,
  analyticsPageFromPath,
  type AvailabilitySlot,
  type AnalyticsPageId,
} from "@/services/api/client";

const FALLBACK_TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00",
];

interface ContactSalesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
  defaultPhone?: string;
  onConfirmed?: () => void;
  showBranding?: boolean;
  /** Overrides route-derived page for booking analytics (page_view / booking_*). */
  analyticsPage?: AnalyticsPageId;
}

export default function ContactSalesModal({
  open,
  onOpenChange,
  defaultEmail = "",
  defaultPhone = "",
  onConfirmed,
  showBranding = false,
  analyticsPage,
}: ContactSalesModalProps) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const resolvedPage = useMemo(
    () => analyticsPage ?? analyticsPageFromPath(pathname),
    [analyticsPage, pathname]
  );
  const prevOpenRef = useRef(false);
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const availableDatesSet = useMemo(
    () => new Set(slots.map((s) => s.startDateTime.slice(0, 10))),
    [slots]
  );
  const timeSlotsForSelectedDate = useMemo(() => {
    if (!date || !slots.length) return FALLBACK_TIME_SLOTS;
    const dateStr = format(date, "yyyy-MM-dd");
    const times = slots
      .filter((s) => s.startDateTime.startsWith(dateStr))
      .map((s) => s.startDateTime.slice(11, 16))
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();
    return times.length ? times : FALLBACK_TIME_SLOTS;
  }, [date, slots]);

  // Sync defaults and fetch availability when modal opens
  useEffect(() => {
    if (open) {
      void trackAnalyticsEvent("booking_started", resolvedPage);
      setEmail(defaultEmail);
      setPhone(defaultPhone);
      setConfirmed(false);
      setErrors({});
      setDate(undefined);
      setTime("");
      setDatePickerOpen(false);
      setSlotsError(null);
      setErrors({});
      setSubmitting(false);
      setSlotsLoading(true);
      getBookingAvailability()
        .then((res) => {
          setSlots(res.slots ?? []);
        })
        .catch((err) => {
          setSlotsError(err instanceof Error ? err.message : "Failed to load availability");
          setSlots([]);
        })
        .finally(() => setSlotsLoading(false));
    }
  }, [open, defaultEmail, defaultPhone, resolvedPage]);

  useEffect(() => {
    if (prevOpenRef.current && !open && !confirmed) {
      void trackAnalyticsEvent("booking_canceled", resolvedPage);
    }
    prevOpenRef.current = open;
  }, [open, confirmed, resolvedPage]);

  // Auto-close after confirmation
  useEffect(() => {
    if (confirmed) {
      const timer = setTimeout(() => {
        onOpenChange(false);
        onConfirmed?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [confirmed, onOpenChange, onConfirmed]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!email.trim()) e.email = t("contactSales.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = t("contactSales.invalidEmail");
    if (!date) e.date = t("contactSales.required");
    if (!time) e.time = t("contactSales.required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const dateStr = date ? format(date, "yyyy-MM-dd") : "";
    const selectedSlot = slots.find(
      (s) => s.startDateTime.startsWith(dateStr) && s.startDateTime.slice(11, 16) === time
    );
    if (slots.length === 0) {
      setErrors((e) => ({ ...e, submit: String(t("contactSales.loadAvailabilityFirst") ?? "Please wait for availability to load or try again.") }));
      return;
    }
    if (!selectedSlot) {
      setErrors((e) => ({ ...e, submit: String(t("contactSales.slotNoLongerAvailable") ?? "This slot is no longer available.") }));
      return;
    }

    setSubmitting(true);
    setErrors((e) => ({ ...e, submit: "" }));
    try {
      await bookMeeting({
        startDateTime: selectedSlot.startDateTime,
        endDateTime: selectedSlot.endDateTime,
        attendeeEmail: email.trim(),
        attendeePhone: phone?.trim() || undefined,
        language: i18n.language?.slice(0, 2) || "en",
      });
      void trackAnalyticsEvent("booking_booked", resolvedPage);
      setConfirmed(true);
    } catch (err) {
      setErrors((e) => ({
        ...e,
        submit: err instanceof Error ? err.message : String(t("contactSales.bookingFailed") ?? "Failed to book meeting."),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
      {showBranding && (
          <div className="flex flex-col items-center pt-2 pb-4 border-b border-border mb-2">
            <BrandLogo imgClassName="h-10 mb-2" />
            <p className="text-xs text-muted-foreground mt-0.5">Sovereign Backup for Microsoft 365</p>
          </div>
        )}
        <AnimatePresence mode="wait">
          {confirmed ? (
            <motion.div
              key="confirmed"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center py-8 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-accent" />
              </div>
              <h3 className="font-display text-xl font-bold text-foreground mb-2">
                {String(t("contactSales.confirmed"))}
              </h3>
              <p className="text-sm text-muted-foreground">
                {date && format(date, "PPP")} {String(t("contactSales.at"))} {time}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{email}</p>
              <p className="text-xs text-muted-foreground mt-4">
                {String(t("contactSales.closingIn"))}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {String(t("contactSales.title"))}
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {String(t("contactSales.subtitle"))}
                </p>
              </DialogHeader>

              <div className="space-y-4 mt-6">
                {slotsLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {String(t("contactSales.loadingAvailability") ?? "Loading availability…")}
                  </div>
                )}
                {slotsError && !slotsLoading && (
                  <p className="text-amber-600 dark:text-amber-500 text-xs py-1">
                    {slotsError}
                  </p>
                )}
                {/* Date picker */}
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    {String(t("contactSales.selectDate"))} *
                  </Label>
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={slotsLoading}
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : String(t("contactSales.pickDate"))}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(selectedDate) => {
                          setDate(selectedDate);
                          setTime("");
                          if (selectedDate) setDatePickerOpen(false);
                        }}
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          if (d < today) return true;
                          if (slots.length > 0) {
                            const dateStr = format(d, "yyyy-MM-dd");
                            return !availableDatesSet.has(dateStr);
                          }
                          return d.getDay() === 0 || d.getDay() === 6;
                        }}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.date && <p className="text-destructive text-xs mt-1">{errors.date}</p>}
                </div>

                {/* Time picker */}
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    {String(t("contactSales.selectTime"))} *
                  </Label>
                  {!date ? (
                    <p className="text-sm text-muted-foreground py-3 border border-dashed border-border rounded-md text-center">
                      {String(t("contactSales.selectDateFirst") ?? "Select a date above to see available times.")}
                    </p>
                  ) : (
                    <div className="grid grid-cols-4 gap-1.5">
                      {timeSlotsForSelectedDate.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setTime(slot)}
                          className={`px-2 py-1.5 rounded-md border text-xs font-medium transition-all ${
                            time === slot
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-card text-foreground border-border hover:border-primary/50"
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                  {errors.time && <p className="text-destructive text-xs mt-1">{errors.time}</p>}
                </div>

                {/* Email */}
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {String(t("contactSales.email"))} *
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@acmecorp.com"
                  />
                  {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {String(t("contactSales.phone"))}
                  </Label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+33 1 23 45 67 89"
                  />
                </div>

                {errors.submit && (
                  <p className="text-destructive text-sm">{errors.submit}</p>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full gap-2 mt-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {String(t("contactSales.booking") ?? "Booking…")}
                    </>
                  ) : (
                    <>
                      {String(t("contactSales.submit"))} <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
